import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import type { PdfNotesApi } from '@/stores/local/shared/PdfNotesStore';

type PdfNotesIndexStoreParams = {
  organizationId: string;
  api: PdfNotesApi;
};

type OpenNotesRequest = {
  token: number;
  documentId: string;
  name?: string;
};

type PrivateFields = '_openNotesRequest';

export class PdfNotesIndexStore implements ILocalStore {
  private readonly _organizationId: string;
  private readonly _api: PdfNotesApi;
  private _unsubscribe: (() => void) | null = null;
  private _openNotesRequest: OpenNotesRequest | null = null;
  private _openNotesRequestToken = 0;

  readonly meta = new MetaStore();
  readonly counts = observable.map<string, number>();

  constructor({ organizationId, api }: PdfNotesIndexStoreParams) {
    this._organizationId = organizationId;
    this._api = api;

    makeObservable<PdfNotesIndexStore, PrivateFields>(this, {
      _openNotesRequest: observable.ref,
      hasAnyNotes: computed,
      openNotesRequest: computed,
      load: action.bound,
      requestOpenNotes: action.bound,
      consumeOpenNotesRequest: action.bound,
      clear: action.bound,
    });

    this._unsubscribe = this._api.subscribe((change) => {
      if (change.organizationId !== this._organizationId) {
        return;
      }

      this.load();
    });
  }

  get hasAnyNotes() {
    return this.counts.size > 0;
  }

  get openNotesRequest() {
    return this._openNotesRequest;
  }

  hasNotes(documentId: string): boolean {
    return (this.counts.get(documentId) ?? 0) > 0;
  }

  requestOpenNotes(documentId: string, name?: string) {
    const normalizedDocumentId = String(documentId);

    if (!normalizedDocumentId) {
      return;
    }

    this._openNotesRequestToken += 1;
    this._openNotesRequest = {
      token: this._openNotesRequestToken,
      documentId: normalizedDocumentId,
      name: name?.trim() || undefined,
    };
  }

  consumeOpenNotesRequest(token: number) {
    if (!this._openNotesRequest || this._openNotesRequest.token !== token) {
      return;
    }

    this._openNotesRequest = null;
  }

  async load() {
    this.meta.setLoadedStartMeta();

    const counts = await this._api.getCountsByDocument(this._organizationId);

    runInAction(() => {
      this.counts.clear();
      Object.entries(counts).forEach(([documentId, count]) => {
        if (count > 0) {
          this.counts.set(documentId, count);
        }
      });
      this.meta.setLoadedSuccessMeta();
    });
  }

  clear() {
    this.counts.clear();
    this._openNotesRequest = null;
    this.meta.reset();
  }

  destroy(): void {
    this._unsubscribe?.();
    this._unsubscribe = null;
    this.clear();
    this.meta.destroy();
  }
}
